/* @flow */

import { extend, param, noop } from './coreutils';
import isdebugEnabled from './debug';

/**
 * @description module for logger functions
 * @module utils/logger
 * @requires utils/coreutils
 */

let utilLoggerGif = '';

let gErrorExcludeRX = null;

// console log related settings
let clogStart = 0; // to record session start time

const c = window.console || {};
const cLog = c.log || noop;
const cDebug = c.debug || cLog;
const cInfo = c.info || cLog;
const cError = c.error || cLog;

/**
 * Set log start time. To find diff in console log.
 * Helpful in debugging time related activities
 *
 * @returns {void} Nothing to return here
 */
export function setLoggerStart(): void {
  clogStart = clogStart || Date.now();
}

/**
 * setter for logger gif
 *
 * @param {string} loggerGif - Img URL
 * @returns {void}
 */
export function setLoggerGif(loggerGif: string) {
  utilLoggerGif = loggerGif;
}

/**
 *  @description Setter for error exclusion pattern
 *  @param {string} sExclude Log Exclusion string
 *  @returns {void} - Nothing to return here
 */
export function setErrorExcludeReporting(sExclude: string = '') {
  gErrorExcludeRX = null;
  try {
    gErrorExcludeRX = sExclude.length ? new RegExp(`${sExclude}`) : null;
  } catch {
    /** Ignore exception here */
  }
}

/**
 * Utility function for sending logger gif request (From global.js in EOW)
 *
 * @access private
 * @param {string} cmd - Log message to send
 * @param {object} o - usr & count are not allowed/overwritten here
 * @returns {void}
 */
export function sendLoggerImg(cmd: string, o: anyObject) {
  cmd = cmd || '';
  o = o || {};
  if (!cmd.length || !utilLoggerGif.length) {
    return;
  }

  // deep copy & merging args
  extend(true, o, o, {
    command: cmd,
    count: Date.now()
  });

  // convert into query param
  const beaconUrl = `${utilLoggerGif}?${param(o)}`;
  /* if (sendBeacon(beaconUrl)) {
    return;
  } */

  // sendBeacon is not supported, send image tracking request
  const img = document.createElement('img');
  img.src = beaconUrl;
}

/**
 * Logging utility function
 *
 * @access private
 * @param {string} s - string to be logged
 * @param {boolean} appLogging - Should log to back-end
 * @param {Function} cLogger Console logger fn
 * @returns {void}
 */
function _log(s: string, appLogging: boolean, cLogger: string => void) {
  s = s || '';
  if (isdebugEnabled()) {
    const cElapsed = (Date.now() - (clogStart || Date.now())) / 1000;
    try {
      cLogger(`[${cElapsed.toFixed(3)}s] - ${s}`);
    } catch {
      /** */
    }
  }
  if (appLogging === true) {
    sendLoggerImg('ADLOG', { msg: s });
  }
}

/**
 * function to log debug log
 *
 * @param {string} s - string to be logged
 * @returns {void}
 */
export function logDebug(s: string) {
  s = `AS:D:${s}`;
  _log(s, false, cDebug);
}

/**
 * function to log error log
 *
 * @param {string} s - string to be logged
 * @returns {void}
 */
export function logError(s: string) {
  s = `AS:E:${s}`;
  let appLogging = true;
  if (gErrorExcludeRX && gErrorExcludeRX.test(s)) {
    appLogging = false;
  }
  _log(s, appLogging, cError);
}

/**
 * function to log info log
 *
 * @param {string} s - string to be logged
 * @returns {void}
 */
export function logInfo(s: string) {
  s = `AS:I:${s}`;
  _log(s, true, cInfo);
}
